import React, { useEffect, useState } from "react";
import { api, formatApiError, fmtQty, stockOf, todayStr } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { LineItemsEditor, emptyLine } from "@/components/stock/LineItemsEditor";
import { FormField, WarehouseSelect, SegToggle } from "@/components/stock/FormField";
import { toast } from "sonner";

const blank = () => ({ date: todayStr(), warehouse: "central", mode: "opname", reason: "", lines: [emptyLine()] });
const diffCls = (d) => (d > 0 ? "text-emerald-700" : d < 0 ? "text-red-700" : "text-slate-400");

// Stock opname (enter physical count) or direct +/- adjustment, per warehouse.
export const AdjustmentDialog = ({ open, onClose, items, onSaved }) => {
  const { t } = useLang();
  const [form, setForm] = useState(blank());
  useEffect(() => { if (open) setForm(blank()); }, [open]);
  const set = (k) => (v) => setForm({ ...form, [k]: v });
  const opname = form.mode === "opname";
  const diffOf = (l, sel) => (l.qty === "" ? 0 : opname ? Number(l.qty) - stockOf(sel, form.warehouse) : Number(l.qty));

  const extraCols = [
    { header: t("system_qty"), render: (l, sel) => (sel ? fmtQty(stockOf(sel, form.warehouse)) : "-") },
    { header: t("difference"), render: (l, sel) => { const d = sel ? diffOf(l, sel) : 0; return <span className={`font-semibold ${diffCls(d)}`}>{d > 0 ? "+" : ""}{fmtQty(d)}</span>; } },
  ];

  const save = async () => {
    const ok = form.lines.every((l) => l.item_id && l.qty !== "" && (opname ? Number(l.qty) >= 0 : Number(l.qty) !== 0));
    if (!ok) return toast.error(t("lines_invalid"));
    if (!form.reason.trim()) return toast.error(t("reason_required"));
    try {
      const lines = form.lines.map((l) => ({ item_type: l.item_type, item_id: l.item_id, value: Number(l.qty) }));
      const { data } = await api.post("/stock-adjustments", { ...form, lines });
      toast.success(`${t("saved")} · ${data.doc_no}`);
      onClose();
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="adjustment-dialog">
        <DialogHeader><DialogTitle>{t("adjustment")}</DialogTitle></DialogHeader>
        <SegToggle value={form.mode} onChange={set("mode")} testid="adjust-mode"
                   options={[{ v: "opname", label: t("opname") }, { v: "delta", label: t("delta_mode") }]} />
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <FormField label={t("warehouse")}>
            <WarehouseSelect value={form.warehouse} onChange={set("warehouse")} testid="adjust-warehouse-select" />
          </FormField>
          <FormField label={t("date")}><Input type="date" value={form.date} onChange={(e) => set("date")(e.target.value)} /></FormField>
          <FormField label={t("reason")}>
            <Input value={form.reason} onChange={(e) => set("reason")(e.target.value)} placeholder={opname ? "Stock opname bulanan" : "Barang rusak / salah input"} data-testid="adjust-reason-input" />
          </FormField>
        </div>
        <LineItemsEditor lines={form.lines} onChange={set("lines")} items={items} warehouse={form.warehouse}
                         qtyLabel={opname ? t("physical_qty") : t("delta")} allowNegative={!opname} extraCols={extraCols} testid="adjust-lines" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("cancel")}</Button>
          <Button onClick={save} className="bg-slate-900 hover:bg-slate-800" data-testid="adjust-save-btn">{t("save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
